import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  fetchPricebook, createPbItem, updatePbItem, deletePbItem,
  jaysQuote, retailQuote, fetchCrmJobs, setJobPricebook, fetchPbAudit,
  fetchStaff, createStaff, resetStaffCode, updateStaff, money2,
} from "@/lib/dashboardApi";
import {
  BookOpen, Wrench, Calculator, ShieldCheck, Users2, Lock, Plus, Trash2,
  Pencil, RotateCcw, Save, X, Loader2, AlertTriangle,
} from "lucide-react";

const CC_TIERS = [
  { key: "", label: "No membership" },
  { key: "essential", label: "Essential (15% off)" },
  { key: "advantage", label: "Advantage (20% off)" },
  { key: "elite", label: "Elite (30% off)" },
];
const STAFF_ROLE_OPTS = ["dispatcher", "csr", "service_tech", "installer", "comfort_consultant"];

const groupBy = (items, key) =>
  items.reduce((acc, it) => {
    (acc[it[key]] = acc[it[key]] || []).push(it);
    return acc;
  }, {});

const TABS = [
  { key: "retail", label: "Aquila Retail", icon: BookOpen },
  { key: "jays", label: "Jay's Property Mgmt", icon: Wrench },
  { key: "dispatch", label: "Dispatcher & Audit", icon: Lock },
  { key: "staff", label: "Staff Logins", icon: Users2 },
];

export default function Pricebook() {
  const [tab, setTab] = useState("retail");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await fetchPricebook();
      setData(d);
    } catch (e) {
      console.error(e);
      toast.error("Could not load the pricebook.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data)
    return <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;

  const canEdit = data.can_edit;
  const canToggle = data.can_toggle;
  const visibleTabs = TABS.filter((t) => (t.key === "staff" ? canEdit : true));

  return (
    <div className="max-w-6xl" data-testid="pricebook-panel">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-extrabold text-navy">Dual Pricebook</h1>
        <p className="text-sm text-slate-500 mt-1">
          Two fully separate pricebooks. Aquila never charges overtime, emergency, after-hours or holiday fees.
        </p>
        {!canEdit && (
          <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="pricebook-readonly-note">
            <Lock className="h-3.5 w-3.5" /> View-only — pricing is managed by an admin.
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200">
        {visibleTabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} data-testid={`pricebook-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-colors ${active ? "border-launch text-navy" : "border-transparent text-slate-500 hover:text-navy"}`}>
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "retail" && <RetailTab data={data} canEdit={canEdit} reload={load} />}
      {tab === "jays" && <JaysTab data={data} canEdit={canEdit} reload={load} />}
      {tab === "dispatch" && <DispatchTab canToggle={canToggle} />}
      {tab === "staff" && canEdit && <StaffTab />}
    </div>
  );
}

// ---------------- Retail ----------------
function RetailTab({ data, canEdit, reload }) {
  const items = useMemo(() => data.items.filter((i) => i.book === "retail"), [data]);
  const grouped = groupBy(items, "category");
  const [tier, setTier] = useState("");
  const [picked, setPicked] = useState([]);
  const [quote, setQuote] = useState(null);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const runQuote = async () => {
    if (!picked.length) return toast.error("Pick at least one repair.");
    try { setQuote(await retailQuote({ item_ids: picked, tier: tier || null })); }
    catch (e) { console.error(e); toast.error("Quote failed."); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {Object.entries(grouped).map(([cat, rows]) => (
          <ItemGroup key={cat} title={cat} rows={rows} book="retail" canEdit={canEdit} reload={reload}
            picker={{ picked, toggle }} />
        ))}
        {canEdit && <AddItemButton book="retail" reload={reload} />}
      </div>

      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="cc-discount-preview">
          <p className="font-heading font-bold text-navy flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-launch" /> Comfort Club Discount</p>
          <p className="text-xs text-slate-500 mt-1">Applies to retail repairs only — never to Jay's. Service Call &amp; Diagnostic are excluded.</p>
          <label className="block text-xs font-semibold text-slate-600 mt-4 mb-1">Membership tier</label>
          <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="cc-tier-select"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
            {CC_TIERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <p className="text-xs text-slate-500 mt-3">Check repairs on the left, then quote.</p>
          <button onClick={runQuote} data-testid="cc-quote-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy/90 text-white font-bold py-2.5 rounded-lg text-sm">
            <Calculator className="h-4 w-4" /> Calculate ({picked.length})
          </button>
          {quote && (
            <div className="mt-4 border-t border-slate-100 pt-3 space-y-1.5 text-sm" data-testid="cc-quote-result">
              {quote.lines.map((l, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-slate-600">{l.name}{l.cc_exempt ? " (no discount)" : ""}</span>
                  <span className="text-slate-800">{money2(l.net)}{l.discount > 0 && <span className="text-emerald-600 text-xs ml-1">−{money2(l.discount)}</span>}</span>
                </div>
              ))}
              <div className="flex justify-between font-bold text-navy border-t border-slate-100 pt-2 mt-2">
                <span>Total</span><span data-testid="cc-quote-total">{money2(quote.total)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Jay's ----------------
function JaysTab({ data, canEdit, reload }) {
  const items = useMemo(() => data.items.filter((i) => i.book === "jays"), [data]);
  const grouped = groupBy(items, "category");
  const warrantyParts = items.filter((i) => i.category === "Warranty Parts");
  const pricedItems = items.filter((i) => i.category === "Jay's Priced Items");
  const refr = data.config?.refrigerant || {};
  const refTypes = Object.keys(refr).filter((k) => k !== "note");

  const [form, setForm] = useState({
    billable_minutes: 45, part_cost: 0,
    refrigerant_type: refTypes[0] || "", refrigerant_lbs: 0, refrigerant_allowance_lbs: 0,
    warranty_part_ids: [], priced_item_ids: [],
  });
  const [quote, setQuote] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIn = (k, id) => setForm((f) => ({ ...f, [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id] }));

  const runQuote = async () => {
    try {
      const payload = {
        billable_minutes: Number(form.billable_minutes) || 0,
        part_cost: Number(form.part_cost) || 0,
        refrigerant_type: form.refrigerant_lbs > 0 ? form.refrigerant_type : null,
        refrigerant_lbs: Number(form.refrigerant_lbs) || 0,
        refrigerant_allowance_lbs: Number(form.refrigerant_allowance_lbs) || 0,
        warranty_part_ids: form.warranty_part_ids,
        priced_item_ids: form.priced_item_ids,
      };
      setQuote(await jaysQuote(payload));
    } catch (e) { console.error(e); toast.error("Quote failed."); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {Object.entries(grouped).map(([cat, rows]) => (
          <ItemGroup key={cat} title={cat} rows={rows} book="jays" canEdit={canEdit} reload={reload} />
        ))}
        {canEdit && <AddItemButton book="jays" reload={reload} />}
      </div>

      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="jays-calculator">
          <p className="font-heading font-bold text-navy flex items-center gap-2"><Calculator className="h-4 w-4 text-launch" /> Jay's Job Calculator</p>
          <p className="text-xs text-slate-500 mt-1">Labor: $55 first 45 min + $35 each additional 30 min. Non-warranty part = cost × 2.</p>

          <label className="block text-xs font-semibold text-slate-600 mt-4 mb-1">Billable minutes on site</label>
          <input type="number" min="0" value={form.billable_minutes} onChange={(e) => set("billable_minutes", e.target.value)} data-testid="jays-minutes"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />

          <label className="block text-xs font-semibold text-slate-600 mt-3 mb-1">Non-warranty part cost ($)</label>
          <input type="number" min="0" step="0.01" value={form.part_cost} onChange={(e) => set("part_cost", e.target.value)} data-testid="jays-partcost"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />

          <div className="grid grid-cols-3 gap-2 mt-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Refrigerant</label>
              <select value={form.refrigerant_type} onChange={(e) => set("refrigerant_type", e.target.value)} data-testid="jays-reftype"
                className="w-full border border-slate-300 rounded-lg px-2 py-2 text-sm">
                {refTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Lbs used</label>
              <input type="number" min="0" step="0.1" value={form.refrigerant_lbs} onChange={(e) => set("refrigerant_lbs", e.target.value)} data-testid="jays-reflbs"
                className="w-full border border-slate-300 rounded-lg px-2 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Free lbs</label>
              <input type="number" min="0" step="0.1" value={form.refrigerant_allowance_lbs} onChange={(e) => set("refrigerant_allowance_lbs", e.target.value)} data-testid="jays-refallow"
                className="w-full border border-slate-300 rounded-lg px-2 py-2 text-sm" />
            </div>
          </div>

          {warrantyParts.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs font-semibold text-slate-600 cursor-pointer">Warranty parts</summary>
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {warrantyParts.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-xs text-slate-700">
                    <input type="checkbox" checked={form.warranty_part_ids.includes(p.id)} onChange={() => toggleIn("warranty_part_ids", p.id)} />
                    {p.name} — {money2(p.price)}
                  </label>
                ))}
              </div>
            </details>
          )}
          {pricedItems.length > 0 && (
            <details className="mt-2">
              <summary className="text-xs font-semibold text-slate-600 cursor-pointer">Jay's priced items</summary>
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {pricedItems.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-xs text-slate-700">
                    <input type="checkbox" checked={form.priced_item_ids.includes(p.id)} onChange={() => toggleIn("priced_item_ids", p.id)} />
                    {p.name} — {money2(p.price)} <span className="text-slate-400">/{p.unit}</span>
                  </label>
                ))}
              </div>
            </details>
          )}

          <button onClick={runQuote} data-testid="jays-quote-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy/90 text-white font-bold py-2.5 rounded-lg text-sm">
            <Calculator className="h-4 w-4" /> Calculate Job
          </button>

          {quote && (
            <div className="mt-4 border-t border-slate-100 pt-3 space-y-1.5 text-sm" data-testid="jays-quote-result">
              {quote.lines.map((l, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-slate-600">{l.label}</span><span className="text-slate-800">{money2(l.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between font-bold text-navy border-t border-slate-100 pt-2 mt-2">
                <span>Total</span><span data-testid="jays-quote-total">{money2(quote.total)}</span>
              </div>
              <p className="text-[11px] text-emerald-700 mt-1">{quote.note}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Shared item group + editing ----------------
function ItemGroup({ title, rows, book, canEdit, reload, picker }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 font-heading font-bold text-navy text-sm">{title}</div>
      <div className="divide-y divide-slate-100">
        {rows.map((it) => (
          <ItemRow key={it.id} it={it} book={book} canEdit={canEdit} reload={reload} picker={picker} />
        ))}
      </div>
    </div>
  );
}

function ItemRow({ it, book, canEdit, reload, picker }) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(it.price ?? "");
  const [name, setName] = useState(it.name);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try { await updatePbItem(it.id, { name, price: price === "" ? null : Number(price) }); toast.success("Saved"); setEditing(false); reload(); }
    catch (e) { console.error(e); toast.error("Save failed"); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Remove "${it.name}"?`)) return;
    try { const r = await deletePbItem(it.id); toast.success(r.deactivated ? "Deactivated (seeded item)" : "Deleted"); reload(); }
    catch (e) { console.error(e); toast.error("Delete failed"); }
  };

  if (editing)
    return (
      <div className="flex items-center gap-2 px-4 py-2.5">
        <input value={name} onChange={(e) => setName(e.target.value)} className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm" data-testid={`pb-edit-name-${it.id}`} />
        <div className="flex items-center gap-1"><span className="text-slate-400 text-sm">$</span>
          <input type="number" step="0.01" value={price} placeholder="—" onChange={(e) => setPrice(e.target.value)} className="w-24 border border-slate-300 rounded px-2 py-1 text-sm" data-testid={`pb-edit-price-${it.id}`} /></div>
        <button onClick={save} disabled={busy} className="text-emerald-600 p-1" data-testid={`pb-save-${it.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}</button>
        <button onClick={() => setEditing(false)} className="text-slate-400 p-1"><X className="h-4 w-4" /></button>
      </div>
    );

  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 ${it.active === false ? "opacity-40" : ""}`} data-testid={`pb-row-${it.id}`}>
      {picker && (
        <input type="checkbox" checked={picker.picked.includes(it.id)} onChange={() => picker.toggle(it.id)} disabled={it.price == null} data-testid={`pb-pick-${it.id}`} />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-800 font-medium truncate">{it.name}{it.cc_exempt && <span className="ml-2 text-[10px] font-bold text-amber-600 uppercase">no CC disc</span>}</p>
        {it.note && <p className="text-[11px] text-slate-400 truncate">{it.note}</p>}
      </div>
      <span className="text-sm font-semibold text-navy tabular-nums">{it.price == null ? <span className="text-slate-400 italic">not set</span> : money2(it.price)}</span>
      {it.unit && it.unit !== "each" && <span className="text-[11px] text-slate-400">/{it.unit}</span>}
      {canEdit && (
        <div className="flex items-center gap-1">
          <button onClick={() => setEditing(true)} className="text-slate-400 hover:text-navy p-1" data-testid={`pb-editbtn-${it.id}`}><Pencil className="h-4 w-4" /></button>
          <button onClick={del} className="text-slate-400 hover:text-red-500 p-1" data-testid={`pb-delbtn-${it.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}

function AddItemButton({ book, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "Custom", price: "", unit: "each" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      await createPbItem({ book, name: form.name, category: form.category || "Custom", unit: form.unit || "each", price: form.price === "" ? null : Number(form.price) });
      toast.success("Item added"); setOpen(false); setForm({ name: "", category: "Custom", price: "", unit: "each" }); reload();
    } catch (e) { console.error(e); toast.error("Add failed"); } finally { setBusy(false); }
  };
  if (!open)
    return <button onClick={() => setOpen(true)} data-testid={`pb-add-${book}`} className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-launch"><Plus className="h-4 w-4" /> Add item to {book === "jays" ? "Jay's" : "Retail"}</button>;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid={`pb-addform-${book}`}>
      <div className="grid grid-cols-2 gap-2">
        <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid={`pb-add-name-${book}`} />
        <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm" />
        <input placeholder="Price (blank = TBD)" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid={`pb-add-price-${book}`} />
        <input placeholder="Unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm" />
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-navy text-white text-sm font-bold px-4 py-2 rounded-lg" data-testid={`pb-add-save-${book}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
        <button onClick={() => setOpen(false)} className="text-sm text-slate-500 px-3">Cancel</button>
      </div>
    </div>
  );
}

// ---------------- Dispatcher toggle + audit ----------------
function DispatchTab({ canToggle }) {
  const [jobs, setJobs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [j, a] = await Promise.all([fetchCrmJobs(), fetchPbAudit().catch(() => [])]);
      setJobs(j); setAudit(a);
    } catch (e) { console.error(e); toast.error("Could not load jobs."); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setBook = async (jobId, book) => {
    try { await setJobPricebook(jobId, book, note); toast.success(`Locked to ${book === "jays" ? "Jay's" : "Retail"} pricebook`); setNote(""); load(); }
    catch (e) { console.error(e); toast.error(e.response?.data?.detail || "Action failed"); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>;

  return (
    <div className="space-y-6">
      {!canToggle && (
        <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5" data-testid="dispatch-noperm">
          <AlertTriangle className="h-4 w-4" /> Only dispatchers and admins can set a job's pricebook. You can view the log below.
        </div>
      )}
      {canToggle && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <label className="block text-xs font-semibold text-slate-600 mb-1">Note for the audit log (optional)</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Jay's property — warranty pricing" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="dispatch-note" />
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="dispatch-jobs">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 font-heading font-bold text-navy text-sm">Service Jobs — Active Pricebook</div>
        {jobs.length === 0 && <p className="px-4 py-6 text-sm text-slate-500">No service jobs yet.</p>}
        <div className="divide-y divide-slate-100">
          {jobs.map((j) => (
            <div key={j.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`dispatch-job-${j.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{j.title}</p>
                <p className="text-xs text-slate-400 truncate">{j.customer_name || "—"} {j.assigned_tech ? `· ${j.assigned_tech}` : ""}</p>
              </div>
              <span className={`text-[11px] font-bold uppercase px-2 py-1 rounded-full ${j.active_pricebook === "jays" ? "bg-indigo-100 text-indigo-700" : j.active_pricebook === "retail" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`dispatch-book-${j.id}`}>
                {j.active_pricebook ? (j.active_pricebook === "jays" ? "Jay's" : "Retail") : "Not set"}{j.pricebook_locked && <Lock className="h-3 w-3 inline ml-1" />}
              </span>
              {canToggle && (
                <div className="inline-flex rounded-lg overflow-hidden border border-slate-300">
                  <button onClick={() => setBook(j.id, "retail")} data-testid={`dispatch-set-retail-${j.id}`}
                    className={`px-3 py-1.5 text-xs font-bold ${j.active_pricebook === "retail" ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>RETAIL</button>
                  <button onClick={() => setBook(j.id, "jays")} data-testid={`dispatch-set-jays-${j.id}`}
                    className={`px-3 py-1.5 text-xs font-bold border-l border-slate-300 ${j.active_pricebook === "jays" ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>JAY'S</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="dispatch-audit">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 font-heading font-bold text-navy text-sm">Pricebook Change Log</div>
        {audit.length === 0 && <p className="px-4 py-6 text-sm text-slate-500">No changes logged yet.</p>}
        <div className="divide-y divide-slate-100">
          {audit.map((a) => (
            <div key={a.id} className="px-4 py-2.5 text-sm flex flex-wrap items-center gap-2">
              <span className="text-slate-800 font-medium">{a.job_title}</span>
              <span className="text-slate-400">{a.from_book ? (a.from_book === "jays" ? "Jay's" : "Retail") : "—"} → <b className="text-navy">{a.to_book === "jays" ? "Jay's" : "Retail"}</b></span>
              <span className="text-xs text-slate-400 ml-auto">{a.actor} ({a.actor_role}) · {new Date(a.ts).toLocaleString()}</span>
              {a.note && <span className="w-full text-xs text-slate-500 italic">“{a.note}”</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- Staff logins ----------------
function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ username: "", name: "", role: "service_tech", title: "", employee_number: "", on_dispatch: false });

  const load = async () => {
    setLoading(true);
    try { setStaff(await fetchStaff()); } catch (e) { console.error(e); toast.error("Could not load staff."); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.username.trim() || !form.name.trim()) return toast.error("Username and name required");
    try { const r = await createStaff(form); toast.success(`Added — login code ${r.default_code}`); setForm({ username: "", name: "", role: "service_tech", title: "", employee_number: "", on_dispatch: false }); load(); }
    catch (e) { console.error(e); toast.error(e.response?.data?.detail || "Add failed"); }
  };
  const reset = async (id) => { try { const r = await resetStaffCode(id); toast.success(`Code reset to ${r.default_code}`); load(); } catch (e) { console.error(e); toast.error("Reset failed"); } };
  const toggleActive = async (s) => { try { await updateStaff(s.id, { active: !s.active }); load(); } catch (e) { console.error(e); toast.error("Update failed"); } };
  const toggleDispatch = async (s) => { try { await updateStaff(s.id, { on_dispatch: !s.on_dispatch }); load(); } catch (e) { console.error(e); toast.error("Update failed"); } };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="staff-list">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 font-heading font-bold text-navy text-sm">Team Logins</div>
        <div className="divide-y divide-slate-100">
          {staff.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`staff-row-${s.username}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800">{s.name} <span className="text-slate-400 font-normal">@{s.username}</span>{s.employee_number && <span className="ml-2 text-[11px] font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">#{s.employee_number}</span>}{s.on_dispatch && <span className="ml-2 text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5">On Dispatch</span>}</p>
                <p className="text-xs text-slate-400">{s.title || s.role}{s.must_change && <span className="ml-2 text-amber-600 font-semibold">code not changed yet</span>}{!s.active && <span className="ml-2 text-red-500 font-semibold">inactive</span>}</p>
              </div>
              <button onClick={() => toggleDispatch(s)} className={`text-xs font-semibold ${s.on_dispatch ? "text-slate-500 hover:text-red-500" : "text-emerald-600"}`} data-testid={`staff-dispatch-${s.username}`}>{s.on_dispatch ? "Remove from dispatch" : "Add to dispatch"}</button>
              <button onClick={() => reset(s.id)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy hover:text-launch" data-testid={`staff-reset-${s.username}`}><RotateCcw className="h-3.5 w-3.5" /> Reset code</button>
              <button onClick={() => toggleActive(s)} className={`text-xs font-semibold ${s.active ? "text-red-500" : "text-emerald-600"}`} data-testid={`staff-active-${s.username}`}>{s.active ? "Deactivate" : "Activate"}</button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="staff-add">
        <p className="font-heading font-bold text-navy text-sm mb-3">Add a team member</p>
        <div className="grid sm:grid-cols-3 gap-2">
          <input placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })} className="border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid="staff-add-username" />
          <input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid="staff-add-name" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid="staff-add-role">
            {STAFF_ROLE_OPTS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <input placeholder="Employee # (optional)" value={form.employee_number} onChange={(e) => setForm({ ...form, employee_number: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid="staff-add-empno" />
          <input placeholder="Title (optional)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid="staff-add-title" />
          <label className="flex items-center gap-2 text-sm text-slate-600 px-1"><input type="checkbox" checked={form.on_dispatch} onChange={(e) => setForm({ ...form, on_dispatch: e.target.checked })} data-testid="staff-add-dispatch" /> Show on Dispatch Board</label>
        </div>
        <button onClick={add} className="mt-3 inline-flex items-center justify-center gap-2 bg-navy text-white text-sm font-bold px-4 py-2 rounded-lg" data-testid="staff-add-btn"><Plus className="h-4 w-4" /> Add team member</button>
        <p className="text-xs text-slate-400 mt-2">New members start with login code <b>1111</b> and set a personal code on first sign-in. Admins can reset a forgotten code anytime with “Reset code”.</p>
      </div>
    </div>
  );
}
